import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { LastSeenChip } from '../components/admin/LastSeenChip';
import LLMBudgetBanner from '../components/admin/LLMBudgetBanner';
import { LoyaltyAdminSection } from '../components/admin/LoyaltyAdminSection';
import { 
  Users, 
  Shield, 
  TrendingUp, 
  Calendar,
  Search,
  MoreVertical,
  Eye,
  UserX,
  Trash2,
  Crown,
  Building2,
  UserPlus,
  LogOut,
  User,
  Key,
  Image,
  Settings,
  FileText,
  ExternalLink,
  BarChart3,
  MousePointerClick,
  ScrollText,
  HelpCircle,
  Clock,
  ArrowDown,
  Timer,
  MessageCircle,
  MessageSquare,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  Send,
  Mail,
  AlertTriangle,
  Activity,
  TrendingDown
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import NotificationBell from '../components/NotificationBell';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_864cc152-5d80-4928-acac-3bcce367b776/artifacts/dhju5nz8_Grassroots%20Coach%20Logo%20260329.png";

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [superAdmins, setSuperAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [coachDetails, setCoachDetails] = useState(null);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ email: '', password: '', name: '' });
  
  // Profile management states
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [profileData, setProfileData] = useState({ name: '', avatar: '' });
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Analytics state
  const [analyticsStats, setAnalyticsStats] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsTrends, setAnalyticsTrends] = useState(null);
  const [scrollDepthStats, setScrollDepthStats] = useState(null);
  const [timeOnPageStats, setTimeOnPageStats] = useState(null);
  const [deviceStats, setDeviceStats] = useState(null);
  const [loyaltyLadderStats, setLoyaltyLadderStats] = useState(null);
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Initialize profile data when user loads. Super admins don't have a
    // season, so their avatar comes from the account-level `profile_photo`.
    // Fall back to the legacy `club_badge` for admins whose photo hasn't
    // been migrated yet (before Phase 4b of the per-season identity split).
    if (user) {
      setProfileData({ 
        name: user.name || '', 
        avatar: user.profile_photo || user.club_badge || '' 
      });
    }
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, coachesRes, teamsRes, adminsRes] = await Promise.all([
        axios.get(`${API}/super-admin/stats`, { headers }),
        axios.get(`${API}/super-admin/coaches`, { headers }),
        axios.get(`${API}/super-admin/teams`, { headers }),
        axios.get(`${API}/super-admin/super-admins`, { headers })
      ]);
      setStats(statsRes.data);
      setCoaches(coachesRes.data);
      setTeams(teamsRes.data);
      setSuperAdmins(adminsRes.data);
      
      // Load analytics
      loadAnalytics();
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const [statsRes, trendsRes, scrollRes, timeRes, deviceRes, ladderRes] = await Promise.all([
        axios.get(`${API}/analytics/stats?days=30`, { headers }),
        axios.get(`${API}/analytics/trends?days=30`, { headers }),
        axios.get(`${API}/analytics/scroll-depth?days=30`, { headers }),
        axios.get(`${API}/analytics/time-on-page?days=30`, { headers }),
        axios.get(`${API}/analytics/device-stats?days=30`, { headers }),
        axios.get(`${API}/analytics/loyalty-ladder?days=30`, { headers })
      ]);
      setAnalyticsStats(statsRes.data);
      setAnalyticsTrends(trendsRes.data);
      setScrollDepthStats(scrollRes.data);
      setTimeOnPageStats(timeRes.data);
      setDeviceStats(deviceRes.data);
      setLoyaltyLadderStats(ladderRes.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!profileData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    setSavingProfile(true);
    try {
      // First upload image if selected
      let avatarUrl = profileData.avatar;
      if (selectedFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', selectedFile);
        
        const uploadRes = await axios.post(`${API}/super-admin/upload-avatar`, formData, {
          headers: { 
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        });
        avatarUrl = uploadRes.data.avatar_url;
        setUploading(false);
      }
      
      // Update profile with name (avatar is already updated by upload endpoint).
      // Save the avatar under `profile_photo` (the account-level personal
      // identity field). We keep sending `club_badge` too for backwards
      // compatibility with any older admin records — safe once Phase 4b
      // migration completes.
      await axios.put(`${API}/super-admin/profile`, {
        name: profileData.name,
        club_badge: avatarUrl,
        profile_photo: avatarUrl
      }, { headers });
      
      updateUser({ name: profileData.name, profile_photo: avatarUrl, club_badge: avatarUrl });
      toast.success('Profile updated successfully');
      setShowEditProfile(false);
      setSelectedFile(null);
      setPreviewUrl(null);
      loadData(); // Refresh super admins list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }
      
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      
      setSelectedFile(file);
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!passwordData.current || !passwordData.new || !passwordData.confirm) {
      toast.error('All fields are required');
      return;
    }
    if (passwordData.new !== passwordData.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.new.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSavingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current,
        new_password: passwordData.new
      }, { headers });
      
      toast.success('Password changed successfully');
      setShowChangePassword(false);
      setPasswordData({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const viewCoachDetails = async (coachId) => {
    try {
      const response = await axios.get(`${API}/super-admin/coaches/${coachId}`, { headers });
      setCoachDetails(response.data);
      setSelectedCoach(coachId);
    } catch (error) {
      toast.error('Failed to load coach details');
    }
  };

  const updateUserStatus = async (userId, status) => {
    try {
      await axios.put(`${API}/super-admin/users/${userId}/status`, { status }, { headers });
      toast.success(`User ${status}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const updateCoachPlan = async (coachId, plan) => {
    try {
      await axios.put(`${API}/super-admin/coaches/${coachId}/plan?plan=${plan}`, {}, { headers });
      toast.success(`Plan updated to ${plan}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update plan');
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This will remove all their data.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/super-admin/users/${userId}`, { headers });
      toast.success('User deleted');
      loadData();
      setSelectedCoach(null);
      setCoachDetails(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const createSuperAdmin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/super-admin/create-super-admin`, newAdmin, { headers });
      toast.success('Super admin created');
      setShowCreateAdmin(false);
      setNewAdmin({ email: '', password: '', name: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create super admin');
    }
  };

  const filteredCoaches = coaches.filter(coach => 
    coach.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    coach.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    coach.team_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getPlanBadge = (plan) => {
    const colors = {
      monthly: 'bg-blue-100 text-blue-700',
      annual: 'bg-emerald-100 text-emerald-700',
      lifetime: 'bg-amber-100 text-amber-700'
    };
    const labels = { monthly: 'Monthly', annual: 'Annual', lifetime: 'Lifetime' };
    return <Badge className={colors[plan] || colors.monthly}>{labels[plan] || plan?.toUpperCase()}</Badge>;
  };

  const getStatusBadge = (status) => {
    const colors = {
      approved: 'bg-emerald-100 text-emerald-700',
      suspended: 'bg-amber-100 text-amber-700',
      disabled: 'bg-red-100 text-red-700'
    };
    return <Badge className={colors[status] || colors.approved}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          {/* Top row: Logo + Title + Profile */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <img 
                src={LOGO_URL} 
                alt="Grassroots Coaching" 
                className="w-9 h-9 sm:w-12 sm:h-12 object-contain shrink-0"
              />
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">Super Admin Dashboard</h1>
                <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">Grassroots Coaching Platform Management</p>
              </div>
            </div>
            
            {/* Notification Bell + Profile Dropdown */}
            <div className="flex items-center gap-1 shrink-0">
              <NotificationBell />
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-2 shrink-0">
                  <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center overflow-hidden">
                    {(user?.profile_photo || user?.club_badge) ? (
                      <img src={user.profile_photo || user.club_badge} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-5 h-5 text-emerald-600" />
                    )}
                  </div>
                  <span className="hidden md:block text-sm font-medium text-gray-700">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2 border-b">
                  <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                  <Badge className="mt-1 bg-emerald-100 text-emerald-700 text-xs">Super Admin</Badge>
                </div>
                <DropdownMenuItem onClick={() => setShowEditProfile(true)} className="cursor-pointer">
                  <User className="w-4 h-4 mr-2" />
                  Edit Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowChangePassword(true)} className="cursor-pointer">
                  <Key className="w-4 h-4 mr-2" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600 focus:text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </div>
          
          {/* Action buttons row */}
          <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-1 -mb-1 scrollbar-hide">
            <Button 
              variant="outline"
              size="sm"
              onClick={() => window.open('/', '_blank')}
              className="shrink-0"
            >
              <ExternalLink className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">View Site</span>
            </Button>
            <Button 
              variant="outline"
              size="sm"
              onClick={() => navigate('/super-admin/content')}
              className="shrink-0"
            >
              <FileText className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">App Content</span>
            </Button>
            <Button 
              variant="outline"
              size="sm"
              onClick={() => navigate('/super-admin/chat')}
              className="shrink-0"
            >
              <MessageCircle className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Live Chat</span>
            </Button>
            <Button 
              variant="outline"
              size="sm"
              onClick={() => navigate('/forum')}
              className="shrink-0"
            >
              <MessageSquare className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Coaches Forum</span>
            </Button>
            <Button 
              variant="outline"
              size="sm"
              onClick={() => navigate('/super-admin/ops')}
              className="shrink-0"
              data-testid="ops-nav-btn"
            >
              <Activity className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Ops</span>
            </Button>
            <Button 
              variant="outline"
              size="sm"
              onClick={() => navigate('/super-admin/audit-log')}
              className="shrink-0"
              data-testid="audit-log-nav-btn"
            >
              <ScrollText className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Audit log</span>
            </Button>
            <Button size="sm" onClick={() => setShowCreateAdmin(true)} className="bg-emerald-600 hover:bg-emerald-700 shrink-0">
              <UserPlus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Add Super Admin</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* LLM Budget Banner */}
        <LLMBudgetBanner />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Coaches</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_coaches || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Teams</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_teams || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Players</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_players || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Recent Signups (30d)</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.recent_signups || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Plan Distribution */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg">Plan Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 sm:gap-8">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-blue-500" />
                <span className="text-sm">Monthly: {stats?.plan_distribution?.monthly || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-emerald-500" />
                <span className="text-sm">Annual: {stats?.plan_distribution?.annual || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-amber-500" />
                <span className="text-sm">Lifetime: {stats?.plan_distribution?.lifetime || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="coaches" className="space-y-4">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="inline-flex w-max sm:w-auto">
              <TabsTrigger value="coaches">Coaches</TabsTrigger>
              <TabsTrigger value="teams">Teams</TabsTrigger>
              <TabsTrigger value="admins">Super Admins</TabsTrigger>
              <TabsTrigger value="analytics">
                <BarChart3 className="w-4 h-4 mr-1" />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="campaigns">
                <Send className="w-4 h-4 mr-1" />
                Campaigns
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Coaches Tab */}
          <TabsContent value="coaches">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <CardTitle>All Coaches</CardTitle>
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search coaches..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Coach</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Team</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Plan</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Players</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCoaches.map((coach) => (
                        <tr key={coach.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-gray-900">{coach.name}</p>
                              <p className="text-sm text-gray-500">{coach.email}</p>
                              <LastSeenChip lastLoginAt={coach.last_login_at} />
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className={coach.team_name ? 'text-gray-900' : 'text-gray-400 italic'}>
                              {coach.team_name || 'Not set up'}
                            </span>
                          </td>
                          <td className="py-3 px-4">{getPlanBadge(coach.plan)}</td>
                          <td className="py-3 px-4">{getStatusBadge(coach.status)}</td>
                          <td className="py-3 px-4">{coach.player_count}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Button variant="ghost" size="sm" onClick={() => viewCoachDetails(coach.id)}><Eye className="w-4 h-4" /></Button>
                              <Select value={coach.plan} onValueChange={(value) => updateCoachPlan(coach.id, value)}>
                                <SelectTrigger className="w-24 h-8"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="free">Free</SelectItem>
                                  <SelectItem value="weekly">Weekly</SelectItem>
                                  <SelectItem value="monthly">Monthly</SelectItem>
                                  <SelectItem value="annual">Annual</SelectItem>
                                  <SelectItem value="lifetime">Lifetime</SelectItem>
                                </SelectContent>
                              </Select>
                              {coach.status === 'approved' ? (
                                <Button variant="ghost" size="sm" className="text-amber-600" onClick={() => updateUserStatus(coach.id, 'suspended')}><UserX className="w-4 h-4" /></Button>
                              ) : (
                                <Button variant="ghost" size="sm" className="text-emerald-600" onClick={() => updateUserStatus(coach.id, 'approved')}>Activate</Button>
                              )}
                              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteUser(coach.id, coach.name)}><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile Card Layout */}
                <div className="lg:hidden space-y-3">
                  {filteredCoaches.map((coach) => (
                    <div key={coach.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{coach.name}</p>
                          <p className="text-sm text-gray-500 truncate">{coach.email}</p>
                          <LastSeenChip lastLoginAt={coach.last_login_at} />
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="shrink-0"><MoreVertical className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => viewCoachDetails(coach.id)}>
                              <Eye className="w-4 h-4 mr-2" />View Details
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => updateCoachPlan(coach.id, 'free')}>Set Free</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateCoachPlan(coach.id, 'weekly')}>Set Weekly</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateCoachPlan(coach.id, 'monthly')}>Set Monthly</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateCoachPlan(coach.id, 'annual')}>Set Annual</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateCoachPlan(coach.id, 'lifetime')}>Set Lifetime</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {coach.status === 'approved' ? (
                              <DropdownMenuItem onClick={() => updateUserStatus(coach.id, 'suspended')} className="text-amber-600">
                                <UserX className="w-4 h-4 mr-2" />Suspend
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => updateUserStatus(coach.id, 'approved')} className="text-emerald-600">
                                Activate
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => deleteUser(coach.id, coach.name)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className={coach.team_name ? 'text-gray-700' : 'text-gray-400 italic'}>{coach.team_name || 'No team'}</span>
                        <span className="text-gray-300">|</span>
                        {getPlanBadge(coach.plan)}
                        {getStatusBadge(coach.status)}
                        <span className="text-gray-500">{coach.player_count} players</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Teams Tab */}
          <TabsContent value="teams">
            <Card>
              <CardHeader>
                <CardTitle>All Teams</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Team Name</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Coach</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Plan</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Players</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Members</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Seasons</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Matches</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teams.map((team) => (
                        <tr key={team.coach_id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium text-gray-900">{team.team_name}</td>
                          <td className="py-3 px-4">
                            <div>
                              <p className="text-gray-900">{team.coach_name}</p>
                              <p className="text-sm text-gray-500">{team.coach_email}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4">{getPlanBadge(team.plan)}</td>
                          <td className="py-3 px-4">{team.player_count}</td>
                          <td className="py-3 px-4">{team.member_count}</td>
                          <td className="py-3 px-4">{team.season_count}</td>
                          <td className="py-3 px-4">{team.match_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile Card Layout */}
                <div className="lg:hidden space-y-3">
                  {teams.map((team) => (
                    <div key={team.coach_id} className="border border-gray-200 rounded-lg p-4 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-gray-900">{team.team_name}</p>
                        {getPlanBadge(team.plan)}
                      </div>
                      <p className="text-sm text-gray-500">{team.coach_name} - {team.coach_email}</p>
                      <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                        <span>{team.player_count} players</span>
                        <span>{team.member_count} members</span>
                        <span>{team.season_count} seasons</span>
                        <span>{team.match_count} matches</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Super Admins Tab */}
          <TabsContent value="admins">
            <Card>
              <CardHeader>
                <CardTitle>Super Administrators</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {superAdmins.map((admin) => (
                    <div key={admin.id} className="flex items-center justify-between gap-2 p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                          <Crown className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{admin.name}</p>
                          <p className="text-sm text-gray-500 truncate">{admin.email}</p>
                        </div>
                      </div>
                      <Badge className="bg-emerald-100 text-emerald-700 shrink-0">Super Admin</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
              {/* Page Views Card */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Eye className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Page Views</p>
                      <p className="text-2xl font-bold">{analyticsStats?.page_views || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Unique Sessions Card */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Users className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Unique Sessions</p>
                      <p className="text-2xl font-bold">{analyticsStats?.unique_sessions || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Total Events Card */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <MousePointerClick className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Events</p>
                      <p className="text-2xl font-bold">{analyticsStats?.total_events || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Period Card */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Calendar className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Period</p>
                      <p className="text-2xl font-bold">Last {analyticsStats?.period_days || 30} days</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Trends Chart */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Traffic Trends (Last 30 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analyticsLoading ? (
                  <p className="text-gray-500">Loading...</p>
                ) : analyticsTrends?.trends?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={analyticsTrends.trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => {
                          const d = new Date(date);
                          return `${d.getDate()}/${d.getMonth() + 1}`;
                        }}
                        fontSize={12}
                      />
                      <YAxis fontSize={12} />
                      <Tooltip 
                        labelFormatter={(date) => {
                          const d = new Date(date);
                          return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
                        }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="page_views" stroke="#3B82F6" strokeWidth={2} name="Page Views" dot={false} />
                      <Line type="monotone" dataKey="unique_sessions" stroke="#10B981" strokeWidth={2} name="Sessions" dot={false} />
                      <Line type="monotone" dataKey="button_clicks" stroke="#8B5CF6" strokeWidth={2} name="Clicks" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-gray-500 text-center py-8">No trend data available yet. Visit the landing page to start tracking.</p>
                )}
              </CardContent>
            </Card>

            {/* Time on Page & Scroll Depth Row */}
            <div className="grid gap-4 md:grid-cols-2 mt-4">
              {/* Time on Page Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Timer className="w-5 h-5" />
                    Time on Page
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analyticsLoading ? (
                    <p className="text-gray-500">Loading...</p>
                  ) : timeOnPageStats ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-blue-50 p-4 rounded-lg text-center">
                          <p className="text-2xl font-bold text-blue-600">{timeOnPageStats.average_time_formatted || '0s'}</p>
                          <p className="text-sm text-gray-500">Avg. Time</p>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg text-center">
                          <p className="text-2xl font-bold text-green-600">{timeOnPageStats.total_sessions || 0}</p>
                          <p className="text-sm text-gray-500">Sessions Tracked</p>
                        </div>
                      </div>
                      {timeOnPageStats.time_distribution?.length > 0 && (
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={timeOnPageStats.time_distribution}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="bucket" fontSize={11} />
                            <YAxis fontSize={12} />
                            <Tooltip />
                            <Bar dataKey="count" fill="#3B82F6" name="Sessions" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No time tracking data yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Scroll Depth Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ArrowDown className="w-5 h-5" />
                    Scroll Depth
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analyticsLoading ? (
                    <p className="text-gray-500">Loading...</p>
                  ) : scrollDepthStats?.scroll_depth?.length > 0 ? (
                    <div className="space-y-4">
                      <div className="flex justify-center">
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={scrollDepthStats.scroll_depth} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" fontSize={12} />
                            <YAxis dataKey="depth" type="category" fontSize={12} width={50} />
                            <Tooltip 
                              formatter={(value, name) => [value, name === 'count' ? 'Users' : name]}
                            />
                            <Bar dataKey="count" fill="#10B981" name="Users" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                        {scrollDepthStats.scroll_depth.map((item, idx) => (
                          <div key={idx} className="bg-gray-50 p-2 rounded">
                            <p className="text-lg font-bold text-gray-700">{item.percentage}%</p>
                            <p className="text-xs text-gray-500">reached {item.depth}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No scroll depth data yet</p>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-4 md:grid-cols-2 mt-4">
              {/* Top CTA Clicks */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MousePointerClick className="w-5 h-5" />
                    Top Button Clicks
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analyticsLoading ? (
                    <p className="text-gray-500">Loading...</p>
                  ) : analyticsStats?.top_cta_clicks?.length > 0 ? (
                    <div className="space-y-3">
                      {analyticsStats.top_cta_clicks.map((cta, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="font-medium text-gray-700">{cta.name.replace(/_/g, ' ')}</span>
                          <Badge variant="secondary">{cta.count} clicks</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No button clicks recorded yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Section Engagement */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ScrollText className="w-5 h-5" />
                    Section Engagement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analyticsLoading ? (
                    <p className="text-gray-500">Loading...</p>
                  ) : analyticsStats?.section_engagement?.length > 0 ? (
                    <div className="space-y-3">
                      {analyticsStats.section_engagement.map((section, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="font-medium text-gray-700 capitalize">{section.section.replace(/_/g, ' ')}</span>
                          <Badge variant="secondary">{section.views} views</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No section views recorded yet</p>
                  )}
                </CardContent>
              </Card>

              {/* FAQ Interactions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5" />
                    FAQ Interactions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analyticsLoading ? (
                    <p className="text-gray-500">Loading...</p>
                  ) : analyticsStats?.faq_interactions?.length > 0 ? (
                    <div className="space-y-3">
                      {analyticsStats.faq_interactions.map((faq, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="font-medium text-gray-700">{faq.question}</span>
                          <Badge variant="secondary">{faq.clicks} clicks</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No FAQ interactions recorded yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Loyalty Ladder Rung Engagement */}
              <Card data-testid="loyalty-ladder-analytics-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-amber-500" />
                    Loyalty Ladder Rungs
                  </CardTitle>
                  <p className="text-xs text-gray-500 mt-1">
                    Which milestone visitors gravitate to. Hover + tap counted once per session per rung. Signals whether Year 10 (destination) or mid-tier rungs actually pull the eye.
                  </p>
                </CardHeader>
                <CardContent>
                  {analyticsLoading ? (
                    <p className="text-gray-500">Loading...</p>
                  ) : loyaltyLadderStats?.rungs?.length > 0 ? (
                    <div className="space-y-2">
                      {loyaltyLadderStats.rungs.map((rung, index) => {
                        const top = index === 0;
                        return (
                          <div
                            key={rung.rung}
                            className={`flex items-center justify-between p-3 rounded-lg ${top ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}
                            data-testid={`loyalty-rung-row-${index}`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              {top && <span className="text-amber-600 text-[10px] font-black uppercase tracking-widest">Top</span>}
                              <span className="font-medium text-gray-700 truncate">{rung.rung}</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Badge variant="secondary" className="gap-1" title="Hovers">
                                <Eye className="w-3 h-3" />
                                {rung.hovers}
                              </Badge>
                              <Badge variant="secondary" className="gap-1" title="Taps">
                                <MousePointerClick className="w-3 h-3" />
                                {rung.taps}
                              </Badge>
                              <span className="text-xs text-gray-500 hidden sm:inline" title="Unique sessions">
                                {rung.unique_sessions} sess.
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      <p className="text-[11px] text-gray-500 pt-2">
                        {loyaltyLadderStats.total_events} total interactions across the last {loyaltyLadderStats.period_days} days.
                      </p>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No rung interactions recorded yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Device Types */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Monitor className="w-5 h-5" />
                    Device Types
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analyticsLoading ? (
                    <p className="text-gray-500">Loading...</p>
                  ) : deviceStats?.device_types ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Monitor className="w-4 h-4 text-blue-500" />
                          <span className="font-medium text-gray-700">Desktop</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{deviceStats.device_types.desktop || 0}</Badge>
                          <span className="text-sm text-gray-500">({deviceStats.device_percentages?.desktop || 0}%)</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Smartphone className="w-4 h-4 text-green-500" />
                          <span className="font-medium text-gray-700">Mobile</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{deviceStats.device_types.mobile || 0}</Badge>
                          <span className="text-sm text-gray-500">({deviceStats.device_percentages?.mobile || 0}%)</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Tablet className="w-4 h-4 text-purple-500" />
                          <span className="font-medium text-gray-700">Tablet</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{deviceStats.device_types.tablet || 0}</Badge>
                          <span className="text-sm text-gray-500">({deviceStats.device_percentages?.tablet || 0}%)</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No device data recorded yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Browsers */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Top Browsers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analyticsLoading ? (
                    <p className="text-gray-500">Loading...</p>
                  ) : deviceStats?.browsers?.length > 0 ? (
                    <div className="space-y-3">
                      {deviceStats.browsers.slice(0, 5).map((browser, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="font-medium text-gray-700">{browser.name}</span>
                          <Badge variant="secondary">{browser.sessions} sessions</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No browser data recorded yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Events by Type */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Events by Type
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analyticsLoading ? (
                    <p className="text-gray-500">Loading...</p>
                  ) : analyticsStats?.events_by_type && Object.keys(analyticsStats.events_by_type).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(analyticsStats.events_by_type).map(([type, count], index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="font-medium text-gray-700 capitalize">{type.replace(/_/g, ' ')}</span>
                          <Badge variant="secondary">{count}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No events recorded yet</p>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={loadAnalytics} disabled={analyticsLoading}>
                {analyticsLoading ? 'Loading...' : 'Refresh Analytics'}
              </Button>
            </div>
          </TabsContent>

          {/* Campaigns Tab — audit log of recent email batches */}
          <TabsContent value="campaigns">
            <CampaignsAuditTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Coach Details Dialog */}
      <Dialog open={!!selectedCoach} onOpenChange={() => { setSelectedCoach(null); setCoachDetails(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Coach Details</DialogTitle>
            <DialogDescription>View detailed information and manage this coach&apos;s account</DialogDescription>
          </DialogHeader>
          {coachDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Name</Label>
                  <p className="font-medium">{coachDetails.name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Email</Label>
                  <p className="font-medium break-all">{coachDetails.email}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Team Name</Label>
                  <p className="font-medium">{coachDetails.team_name || 'Not set'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Plan</Label>
                  <p>{getPlanBadge(coachDetails.plan)}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t">
                <div className="text-center p-3 bg-gray-50 rounded-xl">
                  <p className="text-2xl font-bold text-gray-900">{coachDetails.stats?.player_count || 0}</p>
                  <p className="text-sm text-gray-500">Players</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-xl">
                  <p className="text-2xl font-bold text-gray-900">{coachDetails.stats?.season_count || 0}</p>
                  <p className="text-sm text-gray-500">Seasons</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-xl">
                  <p className="text-2xl font-bold text-gray-900">{coachDetails.stats?.match_count || 0}</p>
                  <p className="text-sm text-gray-500">Matches</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-xl">
                  <p className="text-2xl font-bold text-gray-900">{coachDetails.stats?.training_count || 0}</p>
                  <p className="text-sm text-gray-500">Trainings</p>
                </div>
              </div>

              {coachDetails.team_members?.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">Team Members (Invited Players)</Label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {coachDetails.team_members.map((member) => (
                      <div key={member.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{member.name}</p>
                          <p className="text-xs text-gray-500">{member.email}</p>
                        </div>
                        <Badge className={member.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                          {member.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Loyalty Scheme — Phase 1 admin control */}
              <LoyaltyAdminSection
                coach={coachDetails}
                onUpdated={(loyalty) => setCoachDetails((prev) => prev ? { ...prev, loyalty, loyalty_subscription_start: loyalty.start_date } : prev)}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Super Admin Dialog */}
      <Dialog open={showCreateAdmin} onOpenChange={setShowCreateAdmin}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Super Admin</DialogTitle>
            <DialogDescription>Add another super administrator to the platform</DialogDescription>
          </DialogHeader>
          <form onSubmit={createSuperAdmin} className="space-y-4">
            <div>
              <Label htmlFor="admin-name">Name</Label>
              <Input
                id="admin-name"
                value={newAdmin.name}
                onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="admin-email">Email</Label>
              <Input
                id="admin-email"
                type="email"
                value={newAdmin.email}
                onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="admin-password">Password</Label>
              <Input
                id="admin-password"
                type="password"
                value={newAdmin.password}
                onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                required
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreateAdmin(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                Create Admin
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={showEditProfile} onOpenChange={(open) => {
        setShowEditProfile(open);
        if (!open) {
          // Clean up on close
          setSelectedFile(null);
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
          }
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
            <DialogDescription>Update your profile information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            {/* Avatar Preview */}
            <div className="flex flex-col items-center gap-3">
              <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border-2 border-emerald-200 relative">
                {previewUrl ? (
                  <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                ) : profileData.avatar ? (
                  <img 
                    src={profileData.avatar.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${profileData.avatar}` : profileData.avatar} 
                    alt="" 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <User className="w-12 h-12 text-gray-400" />
                )}
              </div>
              
              {/* Upload Button */}
              <div className="flex gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <span className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-emerald-600 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors">
                    <Image className="w-4 h-4" />
                    {selectedFile ? 'Change Image' : 'Upload Image'}
                  </span>
                </label>
                {(selectedFile || previewUrl) && (
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={handleRemoveImage}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              
              {selectedFile && (
                <p className="text-xs text-gray-500">{selectedFile.name}</p>
              )}
              <p className="text-xs text-gray-400">Max 5MB. JPEG, PNG, GIF, or WebP</p>
            </div>
            
            <div>
              <Label htmlFor="profile-name">Name</Label>
              <Input
                id="profile-name"
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                placeholder="Your name"
                required
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEditProfile(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={savingProfile || uploading} 
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {uploading ? 'Uploading...' : savingProfile ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Enter your current password and a new password</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                value={passwordData.current}
                onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={passwordData.new}
                onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={passwordData.confirm}
                onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                required
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowChangePassword(false);
                  setPasswordData({ current: '', new: '', confirm: '' });
                }} 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={savingPassword} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {savingPassword ? 'Changing...' : 'Change Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─── Campaigns audit tab ─────────────────────────────────────────────────

const CAMPAIGN_META = {
  trial_7day:     { label: '7-day trial reminder',  color: 'bg-blue-100 text-blue-800',   sourceLabel: 'Scheduler' },
  trial_3day:     { label: '3-day trial reminder',  color: 'bg-blue-100 text-blue-800',   sourceLabel: 'Scheduler' },
  trial_1day:     { label: 'Trial ends tomorrow',   color: 'bg-blue-200 text-blue-900',   sourceLabel: 'Scheduler' },
  renewal_7day:   { label: '7-day renewal reminder', color: 'bg-emerald-100 text-emerald-800', sourceLabel: 'Scheduler' },
  renewal_3day:   { label: '3-day renewal reminder', color: 'bg-emerald-100 text-emerald-800', sourceLabel: 'Scheduler' },
  offer_blast:    { label: 'Offer Reminder Blast',   color: 'bg-amber-100 text-amber-800',  sourceLabel: 'Manual blast' },
  winback:        { label: 'Win-back Blast',         color: 'bg-purple-100 text-purple-800', sourceLabel: 'Manual blast' },
};

function CampaignsAuditTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const res = await axios.get(`${API}/stripe/admin/email-campaigns`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="campaigns-audit-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5" /> Email Campaigns</CardTitle>
          <p className="text-xs text-gray-500 mt-1">
            Audit log of every email batch — trial reminders, renewal reminders, manual offer blasts and win-back blasts.
            Read-only; live data from the reminder log.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="campaigns-refresh-btn">
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </CardHeader>
      <CardContent>
        {loading && !data ? (
          <div className="flex items-center justify-center py-8 text-gray-500 text-sm">
            Loading…
          </div>
        ) : !data || data.campaigns.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <Mail className="w-10 h-10 mb-3 opacity-30" />
            <p className="text-sm">No campaigns sent yet.</p>
            <p className="text-xs mt-1">Trial / renewal reminders fire automatically; offer + win-back blasts kick off from Plans &amp; Billing.</p>
          </div>
        ) : (
          <>
            {data.sparkline && data.sparkline.length > 0 && (
              <div className="mb-4 p-3 rounded-lg border border-gray-100 bg-gradient-to-br from-emerald-50/40 to-white">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[11px] uppercase tracking-wider font-bold text-emerald-700">Emails sent / day · last 14 days</p>
                  <p className="text-[10px] text-gray-400">
                    {data.sparkline[0]?.date} → {data.sparkline[data.sparkline.length - 1]?.date}
                  </p>
                </div>
                <div style={{ width: '100%', height: 80 }}>
                  <ResponsiveContainer>
                    <LineChart data={data.sparkline} margin={{ top: 6, right: 6, bottom: 6, left: 6 }}>
                      <Tooltip
                        contentStyle={{ fontSize: '11px', padding: '6px 8px', borderRadius: '6px' }}
                        labelStyle={{ fontWeight: 600 }}
                        formatter={(val, name) => [val, name === 'sent' ? 'Sent' : 'Failed']}
                      />
                      <Line
                        type="monotone"
                        dataKey="sent"
                        stroke="#059669"
                        strokeWidth={2}
                        dot={{ r: 2, fill: '#059669' }}
                        activeDot={{ r: 4 }}
                        isAnimationActive={false}
                        data-testid="campaigns-sparkline-sent"
                      />
                      <Line
                        type="monotone"
                        dataKey="failed"
                        stroke="#dc2626"
                        strokeWidth={1.5}
                        strokeDasharray="3 3"
                        dot={false}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-500">
                  <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-600 inline-block" /> Sent</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-red-600 inline-block" style={{ borderBottom: '1px dashed #dc2626' }} /> Failed</span>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
              <SuperStat label="All-time sent" value={data.totals.all_time_sent} />
              <SuperStat label="Delivered" value={data.totals.all_time_delivered ?? 0} />
              <SuperStat label="Opened" value={data.totals.all_time_opened ?? 0} />
              <SuperStat label="Bounced" value={data.totals.all_time_bounced ?? 0} negative />
              <SuperStat label="Campaigns" value={data.totals.campaigns_shown} />
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-200">
                  <tr>
                    <th className="text-left py-2 px-3 font-medium text-gray-500">Type</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-500">Date</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-500">Source</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-500">Sent</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-500">Open</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-500">Bounce</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-500">Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {data.campaigns.map((c, i) => {
                    const meta = CAMPAIGN_META[c.reminder_type] || { label: c.reminder_type, color: 'bg-gray-100 text-gray-700', sourceLabel: '—' };
                    const sent = c.success || 0;
                    const opens = c.opened || 0;
                    const bounces = c.bounced || 0;
                    const openRate = sent > 0 ? Math.round((opens / sent) * 100) : null;
                    const bounceRate = sent > 0 ? Math.round((bounces / sent) * 100) : null;
                    return (
                      <tr key={`${c.reminder_type}-${c.date}-${c.period_anchor || i}`} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-3">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${meta.color}`} data-testid={`campaign-row-type-${i}`}>
                            {meta.label}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-gray-700" data-testid={`campaign-row-date-${i}`}>
                          {c.date}
                          <p className="text-[11px] text-gray-400">
                            {new Date(c.first_sent).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </td>
                        <td className="py-3 px-3 text-gray-500 text-xs">{meta.sourceLabel}</td>
                        <td className="py-3 px-3 text-right">
                          <span className="font-semibold text-emerald-700" data-testid={`campaign-row-sent-${i}`}>{sent}</span>
                        </td>
                        <td className="py-3 px-3 text-right" data-testid={`campaign-row-open-${i}`}>
                          {openRate === null ? (
                            <span className="text-gray-300">—</span>
                          ) : (
                            <div>
                              <span className="font-semibold text-blue-700">{openRate}%</span>
                              <p className="text-[10px] text-gray-400">{opens}/{sent}</p>
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-3 text-right" data-testid={`campaign-row-bounce-${i}`}>
                          {bounceRate === null || bounces === 0 ? (
                            <span className="text-gray-300">—</span>
                          ) : (
                            <div>
                              <span className={`font-semibold ${bounceRate > 5 ? 'text-red-700' : 'text-amber-700'}`}>{bounceRate}%</span>
                              <p className="text-[10px] text-gray-400">{bounces}/{sent}</p>
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-3 text-right">
                          {c.errors > 0 ? (
                            <span className="inline-flex items-center gap-1 font-semibold text-red-700" data-testid={`campaign-row-failed-${i}`}>
                              <AlertTriangle className="w-3.5 h-3.5" /> {c.errors}
                            </span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-2">
              {data.campaigns.map((c, i) => {
                const meta = CAMPAIGN_META[c.reminder_type] || { label: c.reminder_type, color: 'bg-gray-100 text-gray-700', sourceLabel: '—' };
                const sent = c.success || 0;
                const opens = c.opened || 0;
                const openRate = sent > 0 ? Math.round((opens / sent) * 100) : null;
                return (
                  <div key={`m-${c.reminder_type}-${c.date}-${c.period_anchor || i}`} className="border border-gray-200 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${meta.color}`}>{meta.label}</span>
                      <p className="text-xs text-gray-500 mt-1">{c.date} · {meta.sourceLabel}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-emerald-700">{sent} sent</p>
                      {openRate !== null && (
                        <p className="text-[11px] text-blue-700">{openRate}% open rate</p>
                      )}
                      {c.errors > 0 && (
                        <p className="text-[11px] text-red-700">{c.errors} failed</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function SuperStat({ label, value, negative }) {
  return (
    <div className={`p-3 rounded-lg border ${negative ? 'border-red-100 bg-red-50/40' : 'border-emerald-100 bg-emerald-50/40'}`}>
      <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${negative ? 'text-red-700' : 'text-emerald-700'}`}>{label}</p>
      <p className={`text-xl font-bold ${negative ? 'text-red-900' : 'text-emerald-900'}`}>{value}</p>
    </div>
  );
}

